/* eslint-disable @nx/enforce-module-boundaries */
import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  IconButton,
  LinkButton,
  SeeMore,
  Stack,
  Text,
  Tooltip,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useRef } from 'react';
import { MyProfileContext } from '../context';
import {
  AddCircle,
  AddCircleFIlled,
  EarthGlobeIcon,
  OnlyMeLockIcon,
  TeamIcon,
} from '@mybridge/icons';
import { EditIcon } from '@mybridge/icons/Edit';
import { MyProfileIntroductionModal } from '../introduction-modal';
import _ from 'lodash';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';
export const MyProfileIntroduction = forwardRef(
  ({ displayCard = false, ...props }, ref) => {
    const {
      userProfileInfo,
      introductionDisc,
      currentPermissionValue,
      setCurrentPermissionValue,
      audienceDisc,
      setPermissionValue,
    } = useContext(MyProfileContext);
    const { t } = useTranslator();
    const { profile_intro: intro, profile_headline: headline } =
      userProfileInfo ?? {};
    const router = useRouter();
    const sectionRef = useRef(null);

    const mode = intro?.length > 0 || headline?.length > 0 ? 'edit' : 'add';

    const introValue = _.get(userProfileInfo, 'field_audience.profile_intro');

    useEffect(() => {
      if (introValue !== undefined) {
        // console.log('Intro value:', introValue);
        setCurrentPermissionValue(introValue);
      } else {
        setCurrentPermissionValue(
          currentPermissionValue ? currentPermissionValue : null
        );
      }
    }, [introValue, userProfileInfo]);

    useEffect(() => {
      // Check if we should open the introduction modal based on query param
      if (router.query.openDisc === 'introduction') {
        introductionDisc?.onOpen?.();
        
        // Clear the query parameter after opening the disclosure
        router.replace(router.pathname, undefined, { shallow: true });
      }
      
      // Check if we should scroll to this section
      if (router.query.scrollTo === 'introduction' && sectionRef.current) {
        // Calculate position to scroll to (middle of viewport)
        const elementRect = sectionRef.current.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        
        // Scroll to the calculated position
        window.scrollTo({
          top: middle,
          behavior: 'smooth'
        });
        
        // Clear the scroll parameter after scrolling
        const { scrollTo, ...rest } = router.query;
        router.replace(
          { pathname: router.pathname, query: rest },
          undefined,
          { shallow: true }
        );
      }
    }, [router.query, introductionDisc]);

    const renderPermissionSwitch = (param) => {
      switch (param) {
        case 1:
          return {
            icon: <EarthGlobeIcon width="16px" height="16px" />,
            text: t('profileDetails.public') || 'Public',
          };
        case 2:
          return {
            icon: <TeamIcon width="16px" height="16px" />,
            text: t('profileDetails.myNetwork') || 'myNetwork',
          };
        case 3:
          return {
            icon: <OnlyMeLockIcon width="16px" height="16px" />,
            text: t('profileDetails.onlyMe') || 'Only me',
          };
        default:
          return {
            icon: <EarthGlobeIcon width="16px" height="16px" />,
            text: t('profileDetails.public') || 'Public',
          };
      }
    };

    return (
      <>
        <Stack ref={sectionRef}>
          <HStack alignItems="flex-start">
            <Stack flex={1}>
              {headline?.length ? (
                <>
                  <Heading
                    size="18px"
                    color="brandPrimary.500"
                    fontWeight="400"
                  >
                    {headline}
                  </Heading>
                </>
              ) : (
                <Heading fontSize="18px" color="#3D5A80" fontWeight="400">
                  {t('profileDetails.aboutYou') || 'About you'}
                </Heading>
              )}
            </Stack>
            {!displayCard && (
              <HStack justifyContent="flex-end">
                <Tooltip
                  label={renderPermissionSwitch(currentPermissionValue)?.text}
                >
                  <IconButton
                    icon={renderPermissionSwitch(currentPermissionValue)?.icon}
                    size="sm"
                    variant="transparent"
                    onClick={() => {
                      audienceDisc.onOpen();
                      setPermissionValue('profile_intro');
                    }}
                  />
                </Tooltip>
                <Tooltip label={t('buttons.edit') || 'Edit'}>
                  <IconButton
                    icon={<EditIcon width="16px" height="16px" />}
                    size="sm"
                    variant="transparent"
                    onClick={(e) => introductionDisc?.onOpen?.()}
                  />
                </Tooltip>
              </HStack>
            )}
          </HStack>
          {/* {headline?.length ? <Divider /> : ''} */}
          <Stack>
            {intro?.length && !introductionDisc?.isOpen ? (
              <>
                {!(displayCard || intro?.length>295) ? (
                  <Box
                    className="event-description"
                    dangerouslySetInnerHTML={{ __html: intro }}
                  ></Box>
                ) : (
                  <SeeMore
                    maxLength={295}
                    maxHeight="85px"
                    text={intro.replace(/<[^>]*>?/gm, '')}
                    className="event-description"
                  />
                )}
              </>
            ) : (
              ''
            )}
          </Stack>
          {mode === 'edit' ? (
            ''
          ) : (
            <Stack>
              <HStack>
                <Button
                  leftIcon={<AddCircleFIlled />}
                  variant="transparent"
                  px={0}
                  color="brandPrimary.500"
                  onClick={(e) => introductionDisc?.onOpen?.()}
                >
                  <Box opacity={0.5}>

                    {t('profileDetails.writeDetailsAboutYourself') || 'Write some details about yourself'}
                  </Box>
                </Button>
              </HStack>
            </Stack>
          )}
        </Stack>
        {introductionDisc?.isOpen && <MyProfileIntroductionModal nonModal />}
      </>
    );
  }
);
